import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCollapseActions } from '../useCollapseActions';
import { codeKeys } from '../queries';

/**
 * Hook that provides a mutation to expand all parent codes of a given code
 * When a code is clicked, this ensures its parents are expanded so it's visible
 * 
 * @param {string|number} projectId - The project ID
 * @returns {Object} Mutation object with mutate function
 */
export function useExpandParents(projectId) {
  const queryClient = useQueryClient();
  const { expandCodeBar } = useCollapseActions(projectId);

  return useMutation({
    mutationKey: ['expandParents', projectId],
    mutationFn: async (codeId) => {
      const parentIds = [];
      const MAX_LOOP_COUNT = 4; // Maximum number of parent levels to traverse
      
      // Traverse up the parent tree to collect all parent IDs
      let currentId = codeId;
      let loopCount = 0;
      
      while (currentId && loopCount < MAX_LOOP_COUNT) {
        // Look up the code from the cache using the correct query key
        const code = queryClient.getQueryData(codeKeys.detail(currentId));

        if (!code) {
          // Code not found in cache, stop traversal
          break;
        }
        
        // If this code has a parent, add it to our list
        if (code.parent_id) {
          parentIds.push(code.parent_id);
          currentId = code.parent_id;
          loopCount++;
        } else {
          // Reached the root, stop traversal
          break;
        }
      }
      
      // Expand all parents, starting from the top-most parent
      // This ensures parents are expanded before their children
      for (const parentId of parentIds.reverse()) {
        await expandCodeBar(parentId);
      }
      
      return { expandedIds: parentIds };
    },
    onError: (error) => {
      console.error('Failed to expand parent codes:', error);
    }
  });
}